import React, { useCallback, useState } from 'react';
import { Button, Box, Typography, Snackbar, Alert, Tooltip, IconButton } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { importBoxesFromCsv, exportBoxesToCsv } from '../services/database.ts';
import { BRAND_COLORS, FONTS } from '../types/index.ts';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface CsvImportExportProps {
  onDataImported: () => void;
}

const CsvImportExport: React.FC<CsvImportExportProps> = ({ onDataImported }) => {
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const result = event.target?.result;
        if (!result) return;

        // Décodage : UTF-8 strict d'abord ; si le fichier vient d'un Excel
        // français enregistré en ANSI, on retombe sur windows-1252 pour ne pas
        // corrompre les accents (é → �).
        let csvContent: string;
        if (result instanceof ArrayBuffer) {
          try {
            csvContent = new TextDecoder('utf-8', { fatal: true }).decode(result);
          } catch {
            csvContent = new TextDecoder('windows-1252').decode(result);
          }
        } else {
          csvContent = result as string;
        }
        // Retirer un éventuel BOM UTF-8 (émis par notre export pour Excel).
        if (csvContent.charCodeAt(0) === 0xfeff) csvContent = csvContent.slice(1);

        const importedBoxes = importBoxesFromCsv(csvContent);

        onDataImported();

        setNotification({
          open: true,
          message: `${importedBoxes.length} boxes successfully imported`,
          severity: 'success',
        });
      } catch (error) {
        setNotification({
          open: true,
          message: `Import error: ${error instanceof Error ? error.message : 'Invalid format'}`,
          severity: 'error',
        });
      }
    };

    reader.onerror = () => {
      setNotification({
        open: true,
        message: 'Could not read the file. Please try again.',
        severity: 'error',
      });
    };

    reader.readAsArrayBuffer(file);
  }, [onDataImported]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleExport = () => {
    try {
      const csvContent = exportBoxesToCsv();

      // Create a blob and a download link. The BOM makes Excel open the file
      // as UTF-8, so accented box names survive the round-trip.
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'killian-boxes.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({
        open: true,
        message: 'Data exported successfully',
        severity: 'success',
      });
    } catch (error) {
      setNotification({
        open: true,
        message: `Export error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const csvHelpText = `
    How to use CSV import/export:
    
    1. First, click "Export Data (CSV)" to download a template
    2. Open the file with Excel or a similar program
    3. Add your boxes following the template format
    4. Save the file as CSV
    5. Import the file using the upload area
    
    This allows you to quickly add multiple boxes instead of adding them manually one by one.
  `;

  return (
    <Box sx={{ my: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Import/Export Boxes
        </Typography>
        <Tooltip title={csvHelpText} arrow placement="right">
          <IconButton size="small" sx={{ ml: 1, color: BRAND_COLORS.primary }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box 
          {...getRootProps()} 
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? BRAND_COLORS.cyan : 'rgba(23,34,46,0.3)',
            borderRadius: 1,
            p: 2,
            backgroundColor: isDragActive ? `${BRAND_COLORS.cyan}14` : BRAND_COLORS.cardStock,
            cursor: 'pointer',
            flex: 1,
            minWidth: 250,
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body1" align="center">
            {isDragActive
              ? 'Drop the CSV file here...'
              : 'Drag and drop a CSV file here, or click to select a file'}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, fontFamily: FONTS.mono }}>
            Only CSV files are accepted
          </Typography>
        </Box>

        <Tooltip title="First export to get a template, then add your boxes and import">
          <Button 
            variant="contained" 
            onClick={handleExport}
            sx={{
              backgroundColor: BRAND_COLORS.primary,
              '&:hover': {
                backgroundColor: `${BRAND_COLORS.primary}DD`,
              },
              alignSelf: 'center',
            }}
          >
            Export Data (CSV)
          </Button>
        </Tooltip>
      </Box>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CsvImportExport; 